// docs/44 places: the bed under a screen that has no land of its own. The fight and the World map
// sound their country (./ambience.ts); everywhere else a screen names a place: a night outside the
// village under the title, the inn where the party gathers, the haven's streets by day or by night,
// the smithy behind the hero forge, the stall where heroes sell, the storyteller's quiet hall, the
// camp after a fight with its fire. A modal names one over its screen (the forge over the World) and
// the place under it returns when it closes. The take is seeded by the World, so a party hears one
// town. Local: only this player hears it.
import { useEffect } from 'react';
import { create } from 'zustand';
import { audio, setLoopGain, startLoop, stopLoop, type SfxKey } from './index';
import { hash32 } from './mix';

/** A place a screen sounds: the bank, its band (a town by day or night), extra layers (a camp's fire). */
export interface Place { key: SfxKey; band?: number; layers?: SfxKey[]; gain?: number }

interface Named { id: number; place?: Place }
const usePlaces = create<{ stack: Named[] }>()(() => ({ stack: [] }));
let nextId = 1;
let seed = '';
let playing: string[] = [];
const FADE_MS = 2200;

const idOf = (i: number) => `place${i ? `-${i}` : ''}`;
const sig = (p: Place | undefined) => (p ? [p.key, p.band ?? '', ...(p.layers ?? [])].join('|') : '');

function apply(p: Place | undefined): void {
  const keys = p ? [p.key, ...(p.layers ?? [])] : [];
  const want = keys.map((k, i) => `${idOf(i)}=${k}#${i === 0 ? p?.band ?? '' : ''}`);
  playing.forEach((w, i) => { if (want[i] !== w) stopLoop(idOf(i), FADE_MS); });
  keys.forEach((k, i) => {
    if (playing[i] === want[i]) { setLoopGain(idOf(i), (p?.gain ?? 1) * (i ? 0.8 : 1), 600); return; }
    const n = audio.bank(k)?.variants.length ?? 0;
    if (!n) return;
    const band = i === 0 ? p?.band : undefined;
    // a band picks inside itself; a plain bank takes the World's own take of the place
    startLoop(idOf(i), k, { ...(band !== undefined ? { band } : { variant: hash32(`${seed}|${k}`) % n }), gain: (p?.gain ?? 1) * (i ? 0.8 : 1), fadeMs: FADE_MS });
  });
  playing = want;
}

let last = '';
usePlaces.subscribe((s) => {
  const top = [...s.stack].reverse().find((n) => n.place)?.place;
  const k = sig(top);
  if (k === last) return;
  last = k;
  apply(top);
});

/** The World a place's take is seeded by (the session's World; '' outside one). */
export function setPlaceSeed(worldKey: string): void { seed = worldKey; }

/** While mounted, this screen or modal sounds `place` (the latest to ask wins); undefined asks for silence. */
export function usePlace(place: Place | undefined): void {
  const k = sig(place);
  useEffect(() => {
    const id = nextId++;
    usePlaces.setState((s) => ({ stack: [...s.stack, { id, place }] }));
    return () => usePlaces.setState((s) => ({ stack: s.stack.filter((n) => n.id !== id) }));
  }, [k]);   // keyed by the place's signature, not its object identity
}

/** The haven by day (band 0) or by night (1): one World week in three finds it after dark (docs/24 clock). */
export const townBand = (week: bigint | undefined): number => (week !== undefined && week % 3n === 2n ? 1 : 0);
